import React, { ReactElement, CSSProperties } from 'react';
import { Layout as ReactGridLayout } from 'react-grid-layout';

import Widget, { WidgetType } from 'domain/entity/widget';
import { LayoutType, LayoutElementType, ConstraintsPerLayoutElement } from 'domain/entity/layout';
import WidgetContainer from 'adapter/in/dashboards/containers/Widget';
import ReactGrid, { GridLayoutElement, GridLayouts } from 'adapter/in/dashboards/components/generic/ReactGrid';
import i18n from 'asset/translation';
import { elementFitsContraints } from 'adapter/in/dashboards/containers/Grid';
import { WidgetBeingResized } from 'adapter/in/dashboards/containers/Grid/useGridView';

export const BREAKPOINTS = { [LayoutType.DESKTOP]: 1200 - 48, [LayoutType.TABLET]: 996 - 48, [LayoutType.MOBILE]: 768 - 48 };
export const CELL_MARGIN = 10;
export const RATIO = 1.5;
export const COLUMNS = { [LayoutType.DESKTOP]: 12, [LayoutType.TABLET]: 6, [LayoutType.MOBILE]: 4 };

export type Breakpoint = LayoutType.DESKTOP | LayoutType.TABLET | LayoutType.MOBILE;

export type GridLayoutsAndWidgetMap = { gridLayouts: GridLayouts; widgetMap: Map<string, Widget> };
type GridProps = {
    dashboardId: string;
    isEditEnabled: boolean;
    widgetMap: Map<string, Widget>;
    gridLayouts: GridLayouts;
    cellSize: {
        width: number;
        height: number;
    };
    backgroundStyle: CSSProperties;
    currentBreakpoint: Breakpoint;
    breakpoints: { [P: string]: number };
    containerWidth: number;
    onWidthChange: (newWidth: number) => void;
    onLayoutChange: (currentLayout: GridLayoutElement[], allLayouts: GridLayouts) => void;
    onResize: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onResizeStop: (layout: ReactGridLayout[], oldItem: ReactGridLayout, newItem: ReactGridLayout) => void;
    onDragStart: () => void;
    onDragStop: () => void;
    onBreakpointChange: (newBreakpoint: Breakpoint) => void;
    wrongSizeElements: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>>;
    widgetBeingResized?: WidgetBeingResized;
};

const Grid = (props: GridProps): ReactElement => {
    const {
        dashboardId,
        cellSize,
        backgroundStyle,
        widgetMap,
        gridLayouts,
        currentBreakpoint,
        breakpoints,
        containerWidth,
        onResize,
        onResizeStop,
        onDragStart,
        onDragStop,
        onWidthChange,
        onLayoutChange,
        onBreakpointChange,
        isEditEnabled,
        wrongSizeElements,
        widgetBeingResized,
    } = props;

    return (
        <>
            <ReactGrid
                gridLayouts={gridLayouts}
                cellMargin={[CELL_MARGIN, CELL_MARGIN]}
                columns={COLUMNS}
                breakpoints={breakpoints}
                rowHeight={cellSize.height}
                backgroundStyle={backgroundStyle}
                containerWidth={containerWidth}
                onDrag={onDragStart}
                onResize={onResize}
                onResizeStop={onResizeStop}
                onDragStop={onDragStop}
                onWidthChange={onWidthChange}
                onLayoutChange={onLayoutChange}
                onBreakpointChange={(newBreakpoint: string) => onBreakpointChange(newBreakpoint as Breakpoint)}
            >
                {getCurrentLayout(gridLayouts, currentBreakpoint)?.map((widgetLayout: GridLayoutElement) => {
                    const isCorrectlyBeingResized =
                        widgetBeingResized && widgetBeingResized.i === widgetLayout.i ? widgetBeingResized.fitsInGrid : true;
                    const isCorrectlySetInGrid = elementFitsContraints(widgetLayout);

                    return (
                        <div key={widgetLayout.i}>
                            <WidgetContainer
                                dashboardId={dashboardId}
                                widget={
                                    widgetLayout.type === LayoutElementType.WIDGET && widgetMap.get(widgetLayout.i)
                                        ? (widgetMap.get(widgetLayout.i) as Widget)
                                        : {
                                              id: widgetLayout.i,
                                              name: '',
                                              type: WidgetType.ANALYSIS,
                                              presetKey: '',
                                              optionFields: [],
                                              configurationFields: [],
                                          }
                                }
                                isEditingLayout={!widgetLayout.static}
                                isEditEnabled={isEditEnabled}
                                layoutType={currentBreakpoint}
                                alertTooltip={getAlertTooltip(widgetLayout.i, wrongSizeElements, currentBreakpoint, isEditEnabled)}
                                sizeIssue={
                                    isCorrectlySetInGrid && isCorrectlyBeingResized
                                        ? undefined
                                        : {
                                              maxHeight: widgetLayout.maxHeight,
                                              maxWidth: widgetLayout.maxWidth,
                                              minHeight: widgetLayout.minHeight,
                                              minWidth: widgetLayout.minWidth,
                                              type: currentBreakpoint,
                                          }
                                }
                            />
                        </div>
                    );
                })}
            </ReactGrid>
        </>
    );
};

const getAlertTooltip = (
    widgetId: string,
    wrongSizeWidgets: Record<LayoutType, Record<string, ConstraintsPerLayoutElement>>,
    currentBreakpoint: Breakpoint,
    isEditEnabled: boolean
): string | undefined => {
    let wrongLayoutTypes: (string | undefined)[] = [];
    if (isEditEnabled && !wrongSizeWidgets[currentBreakpoint][widgetId]) {
        const wrongSizeWidgetsCopy = { ...wrongSizeWidgets };
        delete wrongSizeWidgetsCopy[currentBreakpoint];
        wrongLayoutTypes = Object.keys(wrongSizeWidgetsCopy)
            .map((layoutType) => {
                if (
                    Object.keys(wrongSizeWidgetsCopy[layoutType as LayoutType]).find(
                        (wrongSizeWidgetId: string) => wrongSizeWidgetId === widgetId
                    )
                ) {
                    return i18n.t(`dashboards.layouts.${layoutType.toLowerCase()}SaveFeedback`);
                }
                return undefined;
            })
            .filter((layoutType) => layoutType !== undefined);
    }
    return wrongLayoutTypes.length > 0
        ? i18n.t('widgets.alertWrongSizeOnLayouts', {
              layoutTypes: wrongLayoutTypes.toString().toLowerCase().replaceAll(',', ', '),
          })
        : undefined;
};

const getCurrentLayout = (reactGridLayouts: GridLayouts, breakpoint: LayoutType) =>
    reactGridLayouts[breakpoint] ?? reactGridLayouts[LayoutType.DESKTOP];

export default Grid;
